import { useState, useEffect, useMemo } from 'react';
import { purchasingAPI, fetchAllPages, GoodsReceiptNote, POLineSchedule } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Loader2, Search, Link2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../utils/errorUtils';

interface LinkScheduleModalProps {
  /** The unallocated GRN being linked. */
  grn: GoodsReceiptNote;
  onClose: () => void;
  /** Called after a successful link so the parent can refresh its list. */
  onLinked: () => void;
}

/**
 * Superuser manual-override modal: pick one of the GRN's PO-line schedule
 * lines and link the whole GRN to it. Linking allocates the GRN's full
 * received qty to that schedule and reconciles only that line (no sibling
 * erosion) — the backend enforces the same-PO-line and non-line-0 rules.
 */
export default function LinkScheduleModal({ grn, onClose, onLinked }: LinkScheduleModalProps) {
  const { formatDate } = useFormatters();
  const [schedules, setSchedules] = useState<POLineSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        const all = await fetchAllPages<POLineSchedule>((page) => purchasingAPI.getSchedules({
          po_line: grn.po_line,
          ordering: 'schedule_date',
          page_size: 100,
          page,
        }));
        if (cancelled) return;
        // Backend rejects synthetic line-0 anchors, so hide them here too.
        const rows = all.filter(
          (s) => s.schedule_line_number !== '0' && s.schedule_date,
        );
        setSchedules(rows);
      } catch (err: unknown) {
        if (!cancelled) setError(getApiErrorMessage(err, 'Failed to load schedule lines'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [grn.po_line]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return schedules;
    return schedules.filter((s) =>
      [s.schedule_line_number, s.schedule_date, s.status, formatDate(s.schedule_date)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [schedules, search, formatDate]);

  const handleLink = async () => {
    if (!selectedId) return;
    setLinking(true);
    try {
      const res = await purchasingAPI.linkGRNToSchedule(grn.id, selectedId);
      toast.success(
        `GRN ${grn.grn_number} linked to schedule line ${
          schedules.find((s) => s.id === selectedId)?.schedule_line_number ?? selectedId
        } (${res.data.allocated_qty} allocated)`,
      );
      onLinked();
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to link GRN to schedule line'));
    } finally {
      setLinking(false);
    }
  };

  const statusClass = (status: string): string => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-green-100 text-green-800';
      case 'partially_shipped': return 'bg-amber-100 text-amber-800';
      case 'missed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Link GRN {grn.grn_number} to a schedule line
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* GRN summary */}
          <div className="rounded-lg border bg-gray-50 p-3 text-sm grid grid-cols-2 gap-x-6 gap-y-1">
            <div><span className="text-gray-500">PO / Line:</span> {grn.po_number} / {grn.line_number}</div>
            <div><span className="text-gray-500">Part:</span> {grn.part_number}</div>
            <div><span className="text-gray-500">Supplier:</span> {grn.supplier_name}</div>
            <div><span className="text-gray-500">Receipt date:</span> {formatDate(grn.receipt_date)}</div>
            <div><span className="text-gray-500">Qty received:</span> <strong>{grn.quantity_received}</strong></div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-8"
              placeholder="Search by date, line #, or status…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Schedule list */}
          <div className="max-h-80 overflow-y-auto rounded-lg border">
            {loading ? (
              <div className="flex items-center justify-center p-6 text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading schedule lines…
              </div>
            ) : error ? (
              <div className="flex items-center gap-2 p-4 text-red-600">
                <AlertCircle className="h-4 w-4" /> {error}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No schedule lines available on this PO line to link to.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-100 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2">Line #</th>
                    <th className="px-3 py-2">Schedule date</th>
                    <th className="px-3 py-2 text-right">Sched qty</th>
                    <th className="px-3 py-2 text-right">Shipped</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr
                      key={s.id}
                      onClick={() => setSelectedId(s.id)}
                      className={`cursor-pointer border-t hover:bg-blue-50 ${
                        selectedId === s.id ? 'bg-blue-100' : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="radio"
                          checked={selectedId === s.id}
                          onChange={() => setSelectedId(s.id)}
                        />
                      </td>
                      <td className="px-3 py-2">{s.schedule_line_number}</td>
                      <td className="px-3 py-2">{formatDate(s.schedule_date)}</td>
                      <td className="px-3 py-2 text-right">{s.schedule_qty}</td>
                      <td className="px-3 py-2 text-right">{s.shipped_qty}</td>
                      <td className="px-3 py-2 text-right">{s.balance_qty}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={statusClass(s.status)}>{s.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={linking}>Cancel</Button>
          <Button onClick={handleLink} disabled={!selectedId || linking}>
            {linking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
            Link whole GRN to this line
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
